import {
  InteractionType,
  PublicClientApplication,
  type RedirectRequest
} from '@azure/msal-browser';
import type { RouteLocation, Router } from 'vue-router';
import { msalInstance, loginRequest } from '@/auth';
import { useCurrentUser } from '@/composables';
import { parseJwt, type JWT } from '@/util';

const { isCompanyAdmin, isSuperAdmin } = useCurrentUser();

export function registerGuard(router: Router) {
  router.beforeEach(async (to: RouteLocation) => {
    if (to.meta.requiresAuth) {
      const request = {
        ...loginRequest,
        redirectStartPage: to.fullPath
      };
      console.info('request', request);
      const authenticated = await isAuthenticated(
        msalInstance,
        InteractionType.Redirect,
        request,
        to
      );
      console.info('authenticated:', authenticated);
      return authenticated || '/'; // Navigate to login if not authenticated
    }

    return !to.meta.requiresAuth && !to.meta.skipAuthRedirect && isUserLoggedIn(msalInstance)
      ? '/welcome'
      : true;
  });
}

const isUserLoggedIn = (instance: PublicClientApplication): boolean => {
  const accounts = instance.getAllAccounts();

  return accounts?.length > 0 || false;
};

async function isAdmin(token: JWT) {
  console.info('checking admin permission');
  if (token.isCompanyAdmin === 'true' || token.noa_admin === 'true') {
    return true;
  }
  return false;
}

export async function isAuthenticated(
  instance: PublicClientApplication,
  interactionType: InteractionType,
  userLoginRequest: RedirectRequest,
  to: RouteLocation
) {
  // If the application uses redirects for interaction, handleRedirectPromise must be called and awaited on each page load before determining if a user is signed in or not
  return instance
    .handleRedirectPromise()
    .then(async () => {
      if (isUserLoggedIn(instance)) {
        let token: JWT = await instance.acquireTokenSilent(userLoginRequest);
        token = parseJwt(token.accessToken);
        isCompanyAdmin.value = token.isCompanyAdmin === 'true' ? true : false;
        isSuperAdmin.value = token.noa_admin === 'true' ? true : false;
        return to.meta.requiresAdmin ? isAdmin(token) : true;
      }

      // User is not signed in and attempting to access protected route. Sign them in. And we only handle redirection type
      if (interactionType === InteractionType.Redirect) {
        return instance
          .loginRedirect(userLoginRequest)
          .then(() => {
            return true;
          })
          .catch(async (error) => {
            console.info('Logging out (loginRedirect error)', error);
            await instance.logoutRedirect();
            return false;
          });
      }
      return false;
    })
    .catch(async (error) => {
      console.info(`Logging out (an error was caught):`, error);
      await msalInstance.logoutRedirect();
      return false;
    });
}
